import PropTypes from "prop-types"
import React from "react"

import { Container, List, PageItem } from "./styled"
/**
 * A paginator that can display several controls , ommit the number of items and offers a navigation
 */
export const Paginator = ({
  current = 1,
  total = 1,
  numToShow,
  urlPrefix = "",
  leftIcon = "‹",
  rightIcon = "›",
  lastIcon = "»",
  firstIcon = "«",
  spreadIcon = "…",
  onNext,
  onLast,
  onFirst,
  onIndex,
  onPrevious,
  noAnchorNavigation,
  hideSpreadIcon,
  hideFirstAndLastControls,
  showFirstAndLastPage,
  alwaysShowControls,
  LinkComponent,
  linkRenderer,
  firstPageAsPrefix,
  ...props
}) => {
  const notOnStart = current !== 1
  const notOnLast = current !== total
  const hasFirst = !hideFirstAndLastControls && (alwaysShowControls || notOnStart)
  const hasLast = !hideFirstAndLastControls && (alwaysShowControls || notOnLast)
  const hasLeft = alwaysShowControls || notOnStart
  const hasRight = alwaysShowControls || notOnLast

  total = parseInt(total)
  current = parseInt(current)

  const getHandleClick = (target, caller) => (e) => {
    if (caller) {
      caller(e, target)
    }
  }

  const handleOnNext = getHandleClick(current + 1, onNext)
  const handleOnLast = getHandleClick(total, onLast)
  const handleOnFirst = getHandleClick(1, onFirst)
  const handleOnPrevious = getHandleClick(current - 1, onPrevious)

  let start = 1
  let end = total

  if (numToShow) {
    numToShow = parseInt(numToShow)
    const numToLeft = Math.ceil((numToShow - 1) / 2)
    start = Math.max(1, current - numToLeft)
    end = Math.min(total, start + numToShow - 1)
    const totalShown = end - start

    if (totalShown + 1 < numToShow) {
      start = Math.max(1, end - numToShow)
    }
  }

  const getHref = (value) => {
    if (noAnchorNavigation) return

    return `${urlPrefix}${value}`
  }

  const isHidingContentsLeft = start !== 1
  const isHidingContentsRight = end !== total

  const hasSpreadLeft = !hideSpreadIcon && isHidingContentsLeft && (!showFirstAndLastPage || start !== 2)

  const hasSpreadRight = !hideSpreadIcon && isHidingContentsRight && (!showFirstAndLastPage || end !== total - 1)

  const showFirstPage = isHidingContentsLeft && showFirstAndLastPage
  const showLastPage = isHidingContentsRight && showFirstAndLastPage

  const renderItem = ({
    onClick: actualOnClick,
    page,
    isCurrent = false,
    icon = null,
    skipNavigation,
    ...ariaAndData
  }) => {
    let href = skipNavigation ? null : getHref(page)
    const onClick = skipNavigation ? null : actualOnClick

    if (firstPageAsPrefix && page === 1) {
      href = urlPrefix
    }

    if (!LinkComponent && linkRenderer) {
      return linkRenderer({
        href,
        page,
        onClick,
        isCurrent,
        icon,
        ariaAndData,
      })
    }

    const AnchorComponent = LinkComponent || PageItem
    const ariaCurrentAttrib = isCurrent ? { "aria-current": String(isCurrent) } : {}

    return (
      <AnchorComponent onClick={onClick} {...ariaCurrentAttrib} {...ariaAndData} href={href} $page={page}>
        {icon || page}
      </AnchorComponent>
    )
  }

  return (
    <Container role="navigation" {...props}>
      <List>
        {hasFirst && (
          <li>
            {renderItem({
              onClick: handleOnFirst,
              "aria-label": "first",
              role: "button",
              page: 1,
              icon: firstIcon,
            })}
          </li>
        )}
        {hasLeft && (
          <li>
            {renderItem({
              onClick: handleOnPrevious,
              "aria-label": "previous",
              role: "button",
              page: current - 1,
              icon: leftIcon,
              skipNavigation: current === 1,
            })}
          </li>
        )}
        {showFirstPage && (
          <li>
            {renderItem({
              onClick: getHandleClick("1", onIndex),
              "aria-label": "first",
              role: "button",
              page: 1,
            })}
          </li>
        )}
        {hasSpreadLeft && (
          <li>
            <PageItem data-testid="spreadLeft">{spreadIcon}</PageItem>
          </li>
        )}

        {Array.from({ length: end - start + 1 }, (v, k) => {
          const target = k + start
          const isCurrent = target === current
          return (
            <li key={k}>
              {renderItem({
                onClick: getHandleClick(target, onIndex),
                "aria-label": target,
                page: target,
                isCurrent,
              })}
            </li>
          )
        })}

        {hasSpreadRight && (
          <li>
            <PageItem data-testid="spreadRight">{spreadIcon}</PageItem>
          </li>
        )}
        {showLastPage && (
          <li>
            {renderItem({
              onClick: getHandleClick(`${total}`, onIndex),
              "aria-label": "last",
              role: "button",
              page: total,
            })}
          </li>
        )}
        {hasRight && (
          <li>
            {renderItem({
              onClick: handleOnNext,
              "aria-label": "next",
              role: "button",
              page: current + 1,
              icon: rightIcon,
              skipNavigation: current === total,
            })}
          </li>
        )}
        {hasLast && (
          <li>
            {renderItem({
              onClick: handleOnLast,
              "aria-label": "last",
              role: "button",
              page: total,
              icon: lastIcon,
            })}
          </li>
        )}
      </List>
    </Container>
  )
}

Paginator.propTypes = {
  /**
   * Current visited page
   */
  current: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Total pages
   */
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Num of pges to simultaneously show
   */
  numToShow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Prefix of the pagination
   */
  urlPrefix: PropTypes.string,
  /**
   * Icon to decement page
   */
  leftIcon: PropTypes.node,
  /**
   * Icon to increment page
   */
  rightIcon: PropTypes.node,
  /**
   * Icon to go to the latest page
   */
  lastIcon: PropTypes.node,
  /**
   * Icon to go to the first page
   */
  firstIcon: PropTypes.node,
  /**
   * If present will be called with event and the desired page when clicking next
   */
  onNext: PropTypes.func,
  /**
   * If present will be called with event and the desired page when clicking last
   */
  onLast: PropTypes.func,
  /**
   * If present will be called with event and the desired page when clicking first
   */
  onFirst: PropTypes.func,
  /**
   * If present will be called with event and the desired page when clicking the number
   */
  onIndex: PropTypes.func,
  /**
   * If true the anchors will not use urls
   */
  noAnchorNavigation: PropTypes.bool,
  /**
   * The icon to be shown if numbers ommited
   */
  spreadIcon: PropTypes.node,
  /**
   * If true will not try to render the spread when ommiting
   */
  hideSpreadIcon: PropTypes.bool,
  /**
   * If true when numbers ommited will show the first and last as well
   */
  showFirstAndLastPage: PropTypes.bool,
  /**
   * If true will hide the first and last controls
   */
  hideFirstAndLastControls: PropTypes.bool,
  /**
   * If true will never hide navigation controls
   */
  alwaysShowControls: PropTypes.bool,
  /**
   * If passsed the anchor will be rendered , if both LinkComponent and renderComponent are passed LinkComponent takes precedence. Will receive aria and data-props as weel as onClick, href and children
   */
  LinkComponent: PropTypes.elementType,
  /**
   * A render cuntion that received {href, page, isCurrent, onClick, icon?, ariaAndData}
   */
  linkRenderer: PropTypes.func,
  /**
   * If true the first page will be equal to the prefix I.e fooBar/1 will become fooBar/
   */
  firstPageAsPrefix: PropTypes.bool,
}
